import React, { useState, useEffect } from 'react';
import {
  isPriorConsentRegion,
  hasAnsweredBanner,
  savePrefs,
} from '../../utils/privacyConsent';
import { openLegalLink } from '../../utils/openLegalLink';
import { LEGAL_PRIVACY_URL } from '../../utils/legalAcceptance';

// Privacy Choices banner for the EEA, the UK and Switzerland (client, ticket
// 13083; copy from notes 15225 / 15227).
//
// In those regions nonessential tags default to denied (see the Consent Mode
// block in public/index.html) and may only start collecting once the visitor
// makes an affirmative choice here. Everywhere else the banner does not appear:
// those visitors are on the opt-out model and use Settings > Privacy Choices
// and "Do Not Sell or Share" instead.
//
// It no longer opens on arrival. The client: let them finish onboarding, and
// open it when they tick "I agree" on the account-creation screen, if there is
// no saved choice yet. That screen calls openPrivacyBanner(). A saved choice,
// either way, is never re-prompted.
//
// The same banner renders in the browser and inside the app's WebView; only
// the wording differs (the app never says "cookies", and says "app" where the
// browser says "site").

const OPEN_EVENT = 'qushi:open-privacy-banner';

// Opens the banner when it is needed: prior-consent region, nothing saved yet.
export const openPrivacyBanner = () => {
  try {
    if (isPriorConsentRegion() && !hasAnsweredBanner()) {
      window.dispatchEvent(new Event(OPEN_EVENT));
    }
  } catch (e) { }
};

// Only "Privacy Policy" is a link, straight to the analytics and advertising
// section of the policy.
const POLICY_SECTION_URL = `${LEGAL_PRIVACY_URL}#analytics-and-advertising-measurement`;

const inApp = () =>
  !!window.QushiSurface && window.QushiSurface.surface !== 'browser';

// The look is qushi.io's banner (qushi-main-site css/custom.css .consent-banner*):
// the client wants the two banners identical in every way.
const Switch = ({ on, onToggle, label }) => (
  <button
    type="button"
    onClick={onToggle}
    role="switch"
    aria-checked={on}
    aria-label={label}
    className="relative h-6 w-[42px] shrink-0 rounded-full transition-colors"
    style={{ backgroundColor: on ? '#004c50' : '#bfc8c9' }}
  >
    <span
      className="absolute left-[3px] top-[3px] h-[18px] w-[18px] rounded-full bg-white transition-transform"
      style={{ transform: on ? 'translateX(18px)' : 'none' }}
    />
  </button>
);

// Every button in the banner has this one style: by law Reject all and Accept
// all must be identical in weight and size, and the client asked for all of
// them to match the old "Reject all".
// All four stay on one line on a phone too.
const BUTTON = 'min-w-0 flex-1 basis-0 whitespace-nowrap rounded-md border border-[#e0ddd9] bg-transparent px-0.5 py-2 text-[10px] font-semibold text-[#3f4849] min-[361px]:text-[11px] sm:px-1 sm:py-2.5 sm:text-sm';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const open = () => {
      if (isPriorConsentRegion() && !hasAnsweredBanner()) setShowBanner(true);
    };
    window.addEventListener(OPEN_EVENT, open);
    return () => window.removeEventListener(OPEN_EVENT, open);
  }, []);

  // One place to record the choice: it goes to the shared store (so qushi.io and
  // this app agree), reaches every tag through Consent Mode, and starts any tag
  // that was waiting on consent. Do Not Sell or Share is a separate control
  // (client, ticket 13083) and is left as it is; declining advertising here is
  // already enough to keep advertising off.
  const persist = (analyticsConsent, adsConsent) => {
    savePrefs({
      analyticsConsent,
      adsConsent,
    });

    // Legacy keys, kept so nothing that still reads them changes behaviour.
    try {
      localStorage.setItem('cookiePreference', analyticsConsent && adsConsent ? 'all' : 'custom');
      localStorage.setItem('cookieConsent', JSON.stringify({
        essential: true,
        analytics: analyticsConsent,
        marketing: adsConsent,
        timestamp: Date.now(),
      }));
    } catch (e) { }

    setShowBanner(false);
    setShowSettings(false);
  };

  const handleAcceptAll = () => persist(true, true);
  const handleRejectAll = () => persist(false, false);
  const handleSaveChoices = () => persist(analytics, marketing);

  const openPolicy = (e) => {
    e.preventDefault();
    openLegalLink('privacy_policy', POLICY_SECTION_URL);
  };

  if (!showBanner) return null;

  const app = inApp();
  const place = app ? 'app' : 'site';

  const rows = [
    {
      key: 'essential',
      label: 'Essential',
      description: `Required for the ${place} to work. (Always on)`,
      on: true,
      disabled: true,
    },
    {
      key: 'analytics',
      label: 'Analytics',
      description: 'How people use QUSHi, so we can improve it.',
      on: analytics,
      onToggle: () => setAnalytics((v) => !v),
    },
    {
      key: 'advertising',
      label: 'Advertising',
      description: 'Measuring our ads and showing you more relevant ones.',
      on: marketing,
      onToggle: () => setMarketing((v) => !v),
    },
  ];

  const policyLink = (
    <button
      type="button"
      onClick={openPolicy}
      className="text-[#004c50] underline"
    >
      Privacy Policy
    </button>
  );

  // Above the floating page-tips button (GuideLauncher, z-50); still below
  // modals (z-[60]).
  return (
    <div
      role="dialog"
      aria-labelledby="privacy-banner-title"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[55] flex justify-center p-4"
    >
      <div
        className="pointer-events-auto max-h-[calc(100vh-32px)] w-full max-w-[560px] overflow-y-auto rounded-xl border border-[#e0ddd9] bg-[#fbf9f6] px-3.5 py-[18px] font-sans sm:p-6"
        style={{ boxShadow: '0 20px 45px -15px rgba(0, 0, 0, 0.4)' }}
      >
        <h2
          id="privacy-banner-title"
          className="mb-2.5 font-montserrat text-xl font-bold text-[#004c50]"
        >
          Privacy Choices
        </h2>

        <p className="mb-[18px] text-[13px] leading-[1.55] text-[#3f4849]">
          {app ? (
            <>
              QUSHi uses essential data to run the app. With your permission, we
              also use analytics and advertising measurement. You can change your
              choices anytime in Settings. Provider details are in our{' '}
              {policyLink}.
            </>
          ) : (
            <>
              We use essential cookies to run QUSHi. With your permission, we also
              use analytics to see what&apos;s working and improve QUSHi, and
              advertising measurement to learn which campaigns bring traders to
              QUSHi. You can change this anytime in Settings. Provider details are
              in our {policyLink}.
            </>
          )}
        </p>

        {showSettings && (
          <div className="mb-[18px]">
            {rows.map((row) => (
              <div
                key={row.key}
                className="mb-2.5 flex items-start justify-between gap-4 rounded-lg border border-[#e0ddd9] px-3.5 py-3"
              >
                <div className="flex flex-col gap-0.5">
                  <strong className="text-sm font-semibold text-[#1b1c1a]">{row.label}</strong>
                  <span className="text-xs text-[#707979]">{row.description}</span>
                </div>
                {!row.disabled && (
                  <Switch on={row.on} onToggle={row.onToggle} label={row.label} />
                )}
              </div>
            ))}
          </div>
        )}

        {!showSettings ? (
          <div className="flex gap-1 sm:gap-2">
            <button type="button" onClick={() => setShowSettings(true)} className={BUTTON}>
              Manage choices
            </button>
            <button type="button" onClick={handleRejectAll} className={BUTTON}>
              Reject all
            </button>
            <button type="button" onClick={handleAcceptAll} className={BUTTON}>
              Accept all
            </button>
          </div>
        ) : (
          <div className="flex gap-1 sm:gap-2">
            <button type="button" onClick={() => setShowSettings(false)} className={BUTTON}>
              Hide choices
            </button>
            <button type="button" onClick={handleRejectAll} className={BUTTON}>
              Reject all
            </button>
            <button type="button" onClick={handleSaveChoices} className={BUTTON}>
              Save choices
            </button>
            <button type="button" onClick={handleAcceptAll} className={BUTTON}>
              Accept all
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
